/* Print Center state + the prototype's option rules (applyDocDefaults, printer presets,
 * paper → printer sync, 2-per-sheet ⇄ orientation). Pure functions: easy to test. */
import { docDef, isDocKey, LETTER_KINDS, type DocKey } from "./docs";
import { PAPERS, paperFam, type MarginKey } from "./papers";
import { printerDef, type ColorMode } from "./printers";

export type Lang3 = "bi" | "en" | "ar";
export type HeadKind = "full" | "simple" | "pre" | "none";
export type WmKind = "none" | "copy" | "conf" | "draft" | "orig";
export type SizeKind = "small" | "normal" | "large";

export interface PrState {
  doc: DocKey;
  scope: "one" | "all";
  emp: number;
  period: string;
  letter: string;
  date: string;          // optional "as of" date (attendance month, letter date, settlement end)
  printer: string;
  paper: string;
  orient: "portrait" | "landscape";
  margin: MarginKey;
  color: ColorMode;
  lang: Lang3;
  head: HeadKind;
  wm: WmKind;
  copies: number;
  perSheet: 1 | 2;
  size: SizeKind;
  qr: boolean;
  sign: boolean;
  pageNo: boolean;
  stamp: boolean;
  duplex: boolean;
  cw: number;
  ch: number;
  zoom: "fit" | "0.5" | "0.75" | "1";
  dept: string;
}

export const DEFAULT_STATE: PrState = {
  doc: "payslip", scope: "one", emp: 103, period: "", letter: "Salary certificate", date: "",
  printer: "office", paper: "A4", orient: "portrait", margin: "normal", color: "color", lang: "bi", head: "full", wm: "none",
  copies: 1, perSheet: 1, size: "normal", qr: true, sign: true, pageNo: true, stamp: true, duplex: false, cw: 210, ch: 297, zoom: "fit", dept: "",
};

/** applyDocDefaults(): paper/orientation/margin/size for the document; switch printer (and reset colour) if it can't take that paper. */
export function applyDocDefaults(s: PrState): PrState {
  const d = docDef(s.doc), df = d.defaults;
  const first = d.fams[0];
  const paper = df.paper || (first === "roll" ? "R80" : first === "card" ? "CR80" : first === "label" ? "L105" : "A4");
  const n: PrState = { ...s, paper, orient: df.orient || "portrait", margin: df.margin || "normal", size: df.size || s.size, perSheet: 1 };
  const fam = paperFam(paper);
  const pr = printerDef(n.printer);
  if (pr.fam !== "any" && pr.fam !== fam && !(pr.fam === "label" && fam === "labelsheet")) {
    n.printer = fam === "roll" ? "th80" : fam === "card" ? "card" : fam === "label" ? "label" : "office";
    n.color = printerDef(n.printer).preset.color || "color";
  }
  return n;
}

export function setDoc(s: PrState, doc: DocKey): PrState {
  return applyDocDefaults({ ...s, doc });
}

/** prPrinter: apply the printer preset; dot-matrix + wide reports → CF15 portrait. */
export function setPrinter(s: PrState, key: string): PrState {
  const pr = printerDef(key);
  let n: PrState = { ...s, printer: pr.key, ...pr.preset };
  if (!pr.preset.orient) n.orient = docDef(n.doc).defaults.orient || "portrait";
  if (pr.key === "dot" && (n.doc === "register" || n.doc === "gosi" || n.doc === "attendance" || n.doc === "wps")) { n.paper = "CF15"; n.orient = "portrait"; }
  if (!docDef(n.doc).fams.includes(paperFam(n.paper))) n = applyDocDefaults(n);
  if (paperFam(n.paper) !== "page") n.perSheet = 1;
  return n;
}

/** pr.paper bind: choose the printer that matches the paper family. */
export function setPaper(s: PrState, paper: string): PrState {
  if (!PAPERS[paper]) return s;
  const fam = paperFam(paper);
  const n: PrState = { ...s, paper };
  if (fam === "roll") n.printer = paper === "R58" ? "th58" : "th80";
  else if (fam === "card") n.printer = "card";
  else if (fam === "label" || fam === "labelsheet") n.printer = fam === "label" ? "label" : "office";
  else if (/^CF/.test(paper)) n.printer = "dot";
  else if (["th80", "th58", "card", "label"].includes(s.printer)) n.printer = "office";
  if (fam !== "page") n.perSheet = 1;
  return n;
}

export function setOrient(s: PrState, orient: PrState["orient"]): PrState {
  return { ...s, orient, perSheet: orient === "landscape" ? 1 : s.perSheet };
}
export function setPerSheet(s: PrState, per: 1 | 2): PrState {
  return { ...s, perSheet: per, orient: per === 2 ? "portrait" : s.orient };
}
export function setCopies(s: PrState, delta: number): PrState {
  return { ...s, copies: Math.max(1, Math.min(10, s.copies + delta)) };
}

/** Initial state from URL query (doc, emp, period, scope, letter, date) — openPrint(). */
export function fromQuery(q: URLSearchParams, base: PrState = DEFAULT_STATE): PrState {
  let s: PrState = { ...base };
  const doc = q.get("doc");
  if (isDocKey(doc)) s.doc = doc;
  const emp = Number(q.get("emp"));
  if (Number.isInteger(emp) && emp > 0) s.emp = emp;
  const period = q.get("period");
  if (period && /^\d{4}-\d{2}$/.test(period)) s.period = period;
  const scope = q.get("scope");
  if (scope === "one" || scope === "all") s.scope = scope;
  const letter = q.get("letter");
  if (letter) s.letter = letter;
  const date = q.get("date");
  if (date && /^\d{4}-\d{2}(-\d{2})?$/.test(date)) s.date = date;
  if (!docDef(s.doc).fams.includes(paperFam(s.paper)) || doc) s = applyDocDefaults(s);
  return s;
}

/** Letter kind from a `letter` param that may be a kind name or a letter id (resolved by caller). */
export const isLetterKind = (k: string) => LETTER_KINDS.some((x) => x[0] === k);
